import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useAction, useMutation, useQuery } from "convex/react";
import { SecurityRoomViewNew } from "./SecurityRoomViewNew";
import { routeLinkHandler } from "../routeNav";
import {
  Ban,
  Check,
  FileJson,
  Moon,
  Play,
  Plus,
  RefreshCw,
  ShieldAlert,
  Sun,
  X,
} from "lucide-react";
import { subscribeAdminActivity, type ActivityStreamHandle } from "../api/client";
import {
  appendSecurityRoomEvent,
  securityRoomStore,
  setSecurityRoomStreamMode,
} from "../stores/securityRoomStore";
import { toggleTheme as flipTheme } from "../theme";
import type { SecurityRoomCapacity } from "../types";
import { api as convexApi } from "../lib/convexApi";

type QueueKey =
  | "needs-review"
  | "blocked"
  | "dlp"
  | "pr-risk"
  | "high-risk"
  | "memory"
  | "exceptions"
  | "all";

type TimeRange = "all" | "24h" | "7d";

type TriageItem = Record<string, unknown> & {
  id: string;
  __source?: string;
};

type SimulatorState = {
  item: TriageItem;
  kind: string;
  content: string;
  filePath: string;
  result?: Record<string, unknown>;
  error?: string;
};

type ExceptionDraft = {
  item: TriageItem;
  ruleId: string;
  reason: string;
  scope: "repo" | "agent" | "global" | "command";
  repo: string;
  agent: string;
  commandPattern: string;
  ticketUrl: string;
  expiresAt: string; // ISO datetime
  fromEventId: string;
};

const QUEUES: Array<{ key: QueueKey; label: string }> = [
  { key: "needs-review", label: "Needs review" },
  { key: "blocked", label: "Blocked" },
  { key: "dlp", label: "DLP" },
  { key: "pr-risk", label: "PR risk" },
  { key: "high-risk", label: "High-risk sessions" },
  { key: "memory", label: "Memory" },
  { key: "exceptions", label: "Exceptions" },
  { key: "all", label: "All events" },
];

// Map our QueueKey to the snapshot capacity key the backend exposes.
const CAPACITY_KEY_BY_QUEUE: Partial<Record<QueueKey, string>> = {
  "needs-review": "reviewableAsks",
  blocked: "blockedCommands",
  dlp: "dlpHits",
  "pr-risk": "prRiskQueue",
  "high-risk": "highRiskSessions",
  memory: "memoryHighlights",
  exceptions: "exceptions",
};

function getSummary(room: Record<string, unknown>) {
  const summary = room.summary;
  return summary && typeof summary === "object"
    ? (summary as {
        capacity?: Partial<Record<string, SecurityRoomCapacity>>;
        staleExceptions?: number;
        pendingApprovals?: number;
      })
    : null;
}

const TIME_RANGES: Array<{ key: TimeRange; label: string }> = [
  { key: "all", label: "All time" },
  { key: "24h", label: "Last 24h" },
  { key: "7d", label: "Last 7d" },
];

function plusDaysIso(days: number): string {
  return new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString();
}

function truncate(value: string, max = 160): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}

export function SecurityRoomView() {
  // The redesigned shell (SecurityRoomViewNew: sidebar-routed Overview with the
  // activity chart + memory graph, Approvals, DLP, PR-risk, Memory, Audit, …)
  // is now the DEFAULT. The legacy single-page triage view stays reachable at
  // ?preview=legacy as a fallback while the redesign beds in.
  const [useLegacy] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("preview") === "legacy";
  });
  if (!useLegacy) {
    return <SecurityRoomViewNew />;
  }

  const room = securityRoomStore.useStore();
  const [activeQueue, setActiveQueue] = useState<QueueKey>("needs-review");
  const [timeRange, setTimeRange] = useState<TimeRange>("all");
  const [simulator, setSimulator] = useState<SimulatorState | null>(null);
  const [rawItem, setRawItem] = useState<TriageItem | null>(null);
  const [simulating, setSimulating] = useState(false);
  const [exceptionDraft, setExceptionDraft] = useState<ExceptionDraft | null>(null);
  const [exceptionSubmitting, setExceptionSubmitting] = useState(false);
  const [exceptionError, setExceptionError] = useState<string | null>(null);
  const [successPill, setSuccessPill] = useState<string | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  // Spec 5.1 PR 5 — /api/security-room migrated to convex:
  // api.security_room.queries.snapshot. useQuery delivers realtime
  // updates over the WebSocket; the prior refresh() polling layer is
  // collapsed into the reactive subscription. Per
  // [[feedback-spec5-pr54-64-review-patterns]] #14 we MERGE the snapshot
  // into the existing securityRoomStore (instead of wholesale-replacing)
  // so the legacy liveActivityEvents stream and any optimistic local
  // state survive.
  const convexSnapshot = useQuery((convexApi as any).security_room.queries.snapshot);
  // Same admin signal the Admin Console reads. Only admins may attach a
  // command pattern to an exception (the mutation enforces it); the form
  // disables the field rather than letting a member fill it in and collect a
  // 403 on submit.
  const myRole = useQuery((convexApi as any).billing.queries.myRole, {}) as
    | { isAdmin?: boolean }
    | undefined;
  const isOrgAdmin = myRole?.isAdmin === true;
  // `myRole` is undefined until the query resolves. The drawer does NOT block
  // submission on it — gating on the role locked users out entirely whenever
  // the query hung. Instead the pattern field stays blank and disabled while
  // unresolved, and says so, so the user is never shown a value that will not
  // be sent.
  const isRoleLoading = myRole === undefined;
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  useEffect(() => {
    if (!mountedRef.current) return;
    if (convexSnapshot === undefined) return;
    // Map the Convex snapshot to the SecurityRoomSnapshot shape the
    // store + render path expects. We DON'T touch liveActivityEvents
    // (live SSE stream populates that). per #15 the write key
    // `pendingApprovals` matches what selectApprovalsList prefers.
    //
    // C75-1 (parity with SecurityRoomViewNew): hydrate the keys that
    // buildSnapshotItems actually reads — commandAudit, blockedCommands,
    // dlpEvents, prRisks, highRiskSessions, memoryNotes, exceptions.
    // Before this, only approvals were written, so every other queue
    // (and "All events") rendered empty after a reload even when the
    // Convex snapshot contained history.
    const toIso = (n: unknown): string => {
      if (n && (typeof n === "number" || typeof n === "string")) {
        const d = new Date(n as number | string);
        if (!Number.isNaN(d.getTime())) return d.toISOString();
      }
      return new Date().toISOString();
    };

    const commandAudit = (convexSnapshot.recentEvents ?? []).map((e: any) => ({
      // Flatten details first (legacy commandAudit rows were flat:
      // cmd/agent/rule at top level) — explicit keys below win.
      ...(e.details && typeof e.details === "object" ? e.details : {}),
      id: String(e.id),
      type: e.type,
      actor: e.actorUserId ?? null,
      subject: e.subject ?? null,
      recordedAt: toIso(e.at),
      createdAt: toIso(e.at),
    }));
    // mergeTriageItems dedupes by id with the specialized buckets listed
    // before the audit bucket, so these subsets classify into their queues
    // while the audit copy is dropped.
    const blockedCommands = commandAudit.filter((e: any) => e.type === "command.blocked");
    const dlpEvents = commandAudit.filter(
      (e: any) => typeof e.type === "string" && e.type.startsWith("dlp")
    );
    const prRisks = (convexSnapshot.briefs ?? []).map((b: any) => ({
      id: String(b.id),
      // cardTitle reads `title` before falling back to the raw doc id —
      // without it every brief row was titled by its Convex id.
      title: typeof b.prUrl === "string" && b.prUrl ? b.prUrl : `PR risk ${b.score ?? ""}`,
      prUrl: b.prUrl,
      score: b.score,
      summary: b.summary,
      factors: b.factors ?? [],
      recordedAt: toIso(b.createdAt),
      createdAt: toIso(b.createdAt),
    }));
    // Distinct ids so a brief can appear in BOTH the PR-risk and
    // High-risk queues (the id-dedupe in mergeTriageItems would
    // otherwise drop the second membership).
    const highRiskSessions = prRisks
      .filter((b: any) => typeof b.score === "number" && b.score >= 70)
      .map((b: any) => ({ ...b, id: `hr-${b.id}` }));
    const memoryNotes = (convexSnapshot.notes ?? []).map((n: any) => ({
      id: String(n.id),
      title: n.title,
      preview: n.preview,
      recordedAt: toIso(n.createdAt),
      createdAt: toIso(n.createdAt),
    }));
    const exceptions = (convexSnapshot.exceptions ?? []).map((x: any) => ({
      ...x,
      recordedAt: toIso(x.createdAt),
      createdAt: toIso(x.createdAt),
    }));

    securityRoomStore.setState(
      (current) =>
        ({
          ...current,
          error: undefined,
          pendingApprovals: convexSnapshot.pendingApprovals.map((a: any) => ({
            id: a.id,
            status: a.status,
            recordedAt: toIso(a.createdAt),
            createdAt: toIso(a.createdAt),
            agent: a.context?.agent || "",
            repo: a.context?.repo || "",
            command: a.command,
            cmd: a.command,
            rule: a.context?.rule || "",
            requestedBy: a.requestedBy,
          })),
          approvals: convexSnapshot.pendingApprovals.map((a: any) => ({
            id: a.id,
            status: a.status,
            recordedAt: toIso(a.createdAt),
            createdAt: toIso(a.createdAt),
            command: a.command,
            requestedBy: a.requestedBy,
            context: a.context,
          })),
          commandAudit,
          blockedCommands,
          dlpEvents,
          prRisks,
          highRiskSessions,
          memoryNotes,
          exceptions,
          securityExceptions: exceptions,
          // Ad-hoc keys preserved for any other consumer.
          securityRoomNotes: convexSnapshot.notes,
          securityRoomBriefs: convexSnapshot.briefs,
          securityRoomExceptions: convexSnapshot.exceptions,
        }) as Record<string, unknown>
    );
  }, [convexSnapshot]);

  // Spec 5.1 PR 5 — Convex mutations replace the legacy
  // /api/approvals/:id/decision and /api/security-room/exceptions{,/revoke}
  // routes. We hold the hooks here so the JSX handlers below can use them.
  const resolveApprovalMut = useMutation((convexApi as any).approvals.mutations.resolve);
  const createExceptionMut = useMutation((convexApi as any).policy.mutations.createException);

  // SSE reconnect with exponential backoff (1s, 2s, 4s, 8s, 15s cap).
  useEffect(() => {
    let cancelled = false;
    let handle: ActivityStreamHandle | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempt = 0;

    function connect() {
      if (cancelled) return;
      let errored = false;
      handle = subscribeAdminActivity(
        (event) => appendSecurityRoomEvent(event),
        (mode) => {
          setSecurityRoomStreamMode(mode);
          if (mode === "error") {
            if (errored) return;
            errored = true;
            scheduleReconnect();
          }
        }
      );
      setSecurityRoomStreamMode(handle.mode);
      // If the handle reports "error" immediately, also retry.
      if ((handle.mode as string) === "error") {
        if (!errored) {
          errored = true;
          scheduleReconnect();
        }
      } else {
        // Successful connect (sse or poll) — reset backoff.
        attempt = 0;
        setReconnecting(false);
      }
    }

    function scheduleReconnect() {
      if (cancelled) return;
      setReconnecting(true);
      try {
        handle?.close();
      } catch {
        /* ignore */
      }
      handle = null;
      const delay = Math.min(15000, 1000 * Math.pow(2, attempt));
      attempt += 1;
      retryTimer = setTimeout(connect, delay);
    }

    connect();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      try {
        handle?.close();
      } catch {
        /* ignore */
      }
    };
  }, []);

  // refresh() is no longer needed — Convex's useQuery delivers updates
  // reactively. Kept as a no-op wrapper so the existing callsites that
  // still invoke refresh() after a mutation don't break; the reactive
  // path eventually settles. Removing every callsite is a follow-up
  // (line counts make it noisy). errorStatus + formatSecurityRoomError
  // remain available for the live-stream error path.
  async function refresh() {
    // no-op — reactive Convex subscription replaces the imperative fetch
  }

  const decideApproval = useCallback(
    async (id: string, decision: string, note?: string) => {
      // Per [[feedback-spec5-pr54-64-review-patterns]] #16 — only call
      // through when the id is a Convex id. Legacy REST ids (UUID /
      // "appr-…") would trip v.id() validation. The page-level reactive
      // subscription drops the row when status flips to "allowed" /
      // "denied" — no explicit refresh needed.
      // G75-7 / Per [[feedback-spec5-pr54-64-review-patterns]] #16:
      // guard any non-Convex id (legacy `appr-` AND UUIDs / dashed
      // shapes) — Convex `v.id()` validation throws on those. Use the
      // same `includes("-")` heuristic as ChatPanel's isLikelyConvexId.
      // Real Convex ids never contain dashes.
      if (typeof id !== "string" || id.includes("-")) return;
      const convexDecision: "allowed" | "denied" | "cancelled" =
        decision === "approved" || decision === "allowed"
          ? "allowed"
          : decision === "cancelled"
            ? "cancelled"
            : "denied";
      try {
        await resolveApprovalMut({
          id: id as never,
          decision: convexDecision,
          reason: note,
        });
      } catch (err) {
        console.warn("[approvals] resolve failed", err);
      }
    },
    [resolveApprovalMut]
  );

  function toggleTheme() {
    flipTheme();
  }

  function openExceptionFromEvent(item: TriageItem) {
    const ruleId = getString(item, ["ruleId", "rule", "policyRuleId"]);
    const repo = getString(item, ["repository", "repo", "root"]);
    const agent = getString(item, ["agent", "requester", "actor"]);
    const command = getString(item, ["command", "commandPreview", "requestedAction"]);
    setExceptionError(null);
    setExceptionDraft({
      item,
      ruleId,
      reason: "",
      scope: "repo",
      repo,
      agent,
      commandPattern: truncate(command, 240),
      ticketUrl: "",
      expiresAt: plusDaysIso(7),
      fromEventId: item.id,
    });
  }

  async function submitException(draft: ExceptionDraft) {
    setExceptionSubmitting(true);
    setExceptionError(null);
    try {
      const payload: {
        ruleId: string;
        reason: string;
        scope: "repo" | "agent" | "global" | "command";
        expiresAt: string;
        repo?: string;
        agent?: string;
        commandPattern?: string;
        ticketUrl?: string;
        fromEventId?: string;
      } = {
        ruleId: draft.ruleId.trim(),
        reason: draft.reason.trim(),
        scope: draft.scope,
        expiresAt: draft.expiresAt,
      };
      if (draft.repo.trim()) payload.repo = draft.repo.trim();
      if (draft.agent.trim()) payload.agent = draft.agent.trim();
      // Admins only. The draft is SEEDED with the event's command (see the
      // draft factory above), so sending it unconditionally would 403 every
      // non-admin exception — disabling the input is not enough, because
      // `disabled` doesn't clear the value behind it.
      // Sent RAW. The mutation rejects surrounding whitespace rather than
      // rewriting it (a security rule must not quietly become a different rule
      // than the one typed), so trimming here would just move the silent
      // rewrite to the client and the invariant would never hold end to end.
      // The resulting 400 surfaces in the drawer's error line.
      if (isOrgAdmin && draft.commandPattern) {
        payload.commandPattern = draft.commandPattern;
      }
      if (draft.ticketUrl.trim()) payload.ticketUrl = draft.ticketUrl.trim();
      if (draft.fromEventId) payload.fromEventId = draft.fromEventId;
      // Spec 5.1 PR 5 — createException Convex mutation replaces the
      // legacy /api/security-room/exceptions REST POST. Convex shape is
      // {name, rule, category} — rule is the v.any() blob that holds
      // the legacy fields. The reactive snapshot subscription folds the
      // new row into the page state.
      await createExceptionMut({
        name: payload.ruleId,
        rule: payload,
        category: "security-room",
      });
      setExceptionDraft(null);
      setSuccessPill("Exception created");
      window.setTimeout(() => setSuccessPill(null), 2500);
    } catch (error) {
      setExceptionError(error instanceof Error ? error.message : "Failed to create exception");
    } finally {
      setExceptionSubmitting(false);
    }
  }

  const approvals = normalizeList(room.pendingApprovals || room.approvals);
  const liveEvents = normalizeList(room.liveActivityEvents);
  const triageItemsAll = useMemo(
    () => mergeTriageItems(buildSnapshotItems(room), liveEvents),
    [room, liveEvents]
  );
  const triageItems = useMemo(
    () => filterByTimeRange(triageItemsAll, timeRange),
    [triageItemsAll, timeRange]
  );
  const queueCounts = useMemo(() => countQueues(triageItems), [triageItems]);
  const visibleItems = useMemo(
    () => filterQueue(triageItems, activeQueue),
    [activeQueue, triageItems]
  );
  const enforcementCards = useMemo(
    () => normalizeEnforcement(room.enforcement),
    [room.enforcement]
  );

  const lastEvent = triageItems[0];
  const streamLabel = String(room.streamMode || "sse");
  const lastEventLabel = formatTimestamp(
    getString(lastEvent, ["recordedAt", "ts", "createdAt", "at"]) ||
      room.lastEventAt ||
      room.generatedAt
  );

  const summary = getSummary(room);
  const capacityKey = CAPACITY_KEY_BY_QUEUE[activeQueue];
  const capacity: SecurityRoomCapacity | undefined =
    capacityKey && summary?.capacity ? summary.capacity[capacityKey] : undefined;
  const showCapacityHint = Boolean(
    capacity &&
    typeof capacity.total === "number" &&
    typeof capacity.shown === "number" &&
    capacity.total > capacity.shown
  );

  const errorStatusValue = (room as Record<string, unknown>).errorStatus as number | undefined;

  return (
    <div className="security-room-shell">
      <header className="security-room-header">
        <div>
          <p className="eyebrow">Cross-session security operations</p>
          <h1>Security Room</h1>
          <p>Live approvals, blocked actions, policy hits, PR risk, and memory events.</p>
        </div>
        <div className="security-room-actions">
          <a className="button secondary" href="/" onClick={routeLinkHandler("/")}>
            Workbench
          </a>
          <button
            className="theme-toggle"
            type="button"
            aria-label="Toggle theme"
            onClick={toggleTheme}
          >
            <Sun className="icon-sun" />
            <Moon className="icon-moon" />
          </button>
          <button className="button primary" type="button" onClick={() => void refresh()}>
            <RefreshCw size={15} aria-hidden="true" />
            Refresh
          </button>
        </div>
      </header>

      <main className="security-room-main">
        {room.error && (
          <div className="inline-error security-error-banner">
            <span>{room.error}</span>
            {errorStatusValue === 402 ? <ManageSubscriptionButton /> : null}
          </div>
        )}

        {successPill ? (
          <div className="security-success-pill" role="status">
            <Check size={14} aria-hidden="true" />
            {successPill}
          </div>
        ) : null}

        <section className="metric-grid security-metric-grid" aria-label="Security metrics">
          <Metric
            label="Pending approvals"
            value={numberMetric(summary?.pendingApprovals, approvals.length)}
          />
          <Metric label="Reviewable asks" value={queueCounts["needs-review"]} />
          <Metric label="Blocked" value={queueCounts.blocked} />
          <Metric label="DLP" value={queueCounts.dlp} />
          <Metric label="PR risk" value={queueCounts["pr-risk"]} />
          <Metric label="Memory" value={queueCounts.memory} />
          <Metric label="Exceptions" value={queueCounts.exceptions} />
          <Metric label="Stale exceptions" value={summary?.staleExceptions ?? 0} />
          <Metric
            label="Stream"
            value={
              reconnecting ? (
                <span className="security-stream-pill">Reconnecting…</span>
              ) : (
                streamLabel
              )
            }
          />
          <Metric label="Last event" value={lastEventLabel || "none"} />
        </section>

        <section className="security-filter-row" aria-label="Time range filter">
          <div className="security-time-range" role="group" aria-label="Time range">
            {TIME_RANGES.map((range) => (
              <button
                key={range.key}
                type="button"
                className="security-time-range__chip"
                aria-pressed={timeRange === range.key}
                onClick={() => setTimeRange(range.key)}
              >
                {range.label}
              </button>
            ))}
          </div>
        </section>

        <section className="security-queue-strip" aria-label="Security queues">
          {QUEUES.map((queue) => (
            <button
              key={queue.key}
              type="button"
              className="security-queue-chip"
              aria-pressed={activeQueue === queue.key}
              onClick={() => setActiveQueue(queue.key)}
            >
              <span>{queue.label}</span>
              <b>{queueCounts[queue.key]}</b>
            </button>
          ))}
        </section>

        {enforcementCards.length ? (
          <section className="security-enforcement" aria-label="Enforcement posture">
            {enforcementCards.map((card) => (
              <article key={card.id} className="security-enforcement-card">
                <span>{card.label}</span>
                <b>{card.state}</b>
                {card.detail ? <p>{card.detail}</p> : null}
              </article>
            ))}
          </section>
        ) : null}

        <section className="security-section security-triage-section">
          <header>
            <h2>{queueLabel(activeQueue)}</h2>
            <span className={`status-chip ${queueStatusClass(activeQueue)}`}>
              {visibleItems.length}
            </span>
          </header>
          {showCapacityHint ? (
            <p className="security-capacity-hint muted">
              Showing {capacity!.shown} of {capacity!.total}
            </p>
          ) : null}
          <div className="security-list">
            <CardList
              items={visibleItems}
              empty="No events in this queue."
              approvalActions={decideApproval}
              onTestRule={openSimulator}
              onShowRaw={setRawItem}
              onCreateException={openExceptionFromEvent}
            />
          </div>
        </section>

        {simulator ? (
          <SimulatorPanel
            state={simulator}
            busy={simulating}
            onChange={setSimulator}
            onClose={() => setSimulator(null)}
            onRun={() => void runSimulation(simulator)}
          />
        ) : null}

        {rawItem ? <RawPanel item={rawItem} onClose={() => setRawItem(null)} /> : null}

        {exceptionDraft ? (
          <CreateExceptionDrawer
            draft={exceptionDraft}
            isAdmin={isOrgAdmin}
            roleLoading={isRoleLoading}
            busy={exceptionSubmitting}
            error={exceptionError}
            onChange={setExceptionDraft}
            onClose={() => {
              setExceptionDraft(null);
              setExceptionError(null);
            }}
            onSubmit={() => void submitException(exceptionDraft)}
          />
        ) : null}
      </main>
    </div>
  );

  function openSimulator(item: TriageItem) {
    const command = getString(item, ["command", "commandPreview", "requestedAction"]);
    const filePath = getString(item, ["filePath", "path"]);
    const reason = getString(item, ["reason", "summary"]);
    setSimulator({
      item,
      kind: filePath && !command ? "file.write" : "command",
      content: command || reason || "",
      filePath: filePath || "",
    });
  }

  async function runSimulation(current: SimulatorState) {
    // Capture the triage item id at the start so we can ignore the result if
    // the user picked a different item before the request resolved — applying
    // a stale verdict to the new item would mislead the operator. We also use
    // functional setSimulator so concurrent edits to the textarea / kind /
    // filePath while the request is in flight are preserved (the previous
    // `setSimulator({ ...current, ... })` calls clobbered them with the
    // snapshot captured at request-start).
    const requestedItemId = current.item.id;
    setSimulating(true);
    setSimulator((prev) =>
      prev && prev.item.id === requestedItemId
        ? { ...prev, error: undefined, result: undefined }
        : prev
    );
    try {
      // Spec 5.1 PR 2 — the legacy /api/admin/policies/simulate route is
      // 410'd; the Convex equivalent (api.policy.queries.simulate) is
      // scheduled for PR 3. Surface a clear "not yet wired" message
      // rather than firing a stale REST call. Once PR 3 ships, swap
      // this to: const data = await convex.query(api.policy.queries.
      // evaluate, { event: buildSimulatorEvent(current) });
      const _event = buildSimulatorEvent(current);
      void _event;
      throw new Error(
        "Policy simulator is migrating to Convex (api.policy.queries.simulate). Available in Spec 5.1 PR 3."
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : "Simulation failed";
      setSimulator((prev) =>
        prev && prev.item.id === requestedItemId
          ? { ...prev, error: message, result: undefined }
          : prev
      );
    } finally {
      setSimulating(false);
    }
  }
}

function ManageSubscriptionButton() {
  // Spec 7: "Manage subscription" opens the Lemon Squeezy customer portal via
  // the portalUrl action (NOT the removed Clerk Billing plans page). Mirrors
  // PlanBillingTab.handleManage: open a tab synchronously inside the click
  // gesture (opening AFTER the await trips popup blockers), sever the opener,
  // then navigate it once the signed portal URL resolves.
  //
  // portalUrl is admin-gated and needs an active subscription, so it can fail
  // (non-admin 403, LS 5xx, no subscription). On failure we surface the error
  // IN PLACE — the previous behavior set window.location.href to the
  // contact-enterprise page, navigating the operator's entire workbench away
  // on any transient hiccup (confusing + destructive, and a parallel error
  // system vs PlanBillingTab's inline message). The contact form stays
  // reachable as a non-destructive link inside the inline error.
  const portalUrl = useAction(convexApi.billing.checkout.portalUrl);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    // Single flex item inside .security-error-banner (a space-between row):
    // button + error stack vertically so the error never becomes a third
    // sibling that shoves the button to the center of the banner.
    <span className="security-manage-subscription__wrap">
      <button
        type="button"
        className="button secondary security-manage-subscription"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          // Open the tab synchronously inside the click gesture (opening it
          // AFTER the await trips popup blockers). If the browser blocked it,
          // window.open returns null — bail BEFORE the portalUrl action so we
          // don't mint an unused Lemon Squeezy portal URL, and point the
          // operator at the actual cause (a blocked popup, not a portal fault).
          const tab = window.open("about:blank", "_blank");
          if (!tab) {
            setErr("Could not open the billing portal — please allow popups for this site.");
            setBusy(false);
            return;
          }
          tab.opener = null;
          try {
            const res = await portalUrl({});
            if (res?.ok && res.url) {
              tab.location.href = res.url;
            } else {
              tab.close();
              setErr("Could not open the billing portal.");
            }
          } catch {
            tab.close();
            setErr("Could not open the billing portal.");
          } finally {
            setBusy(false);
          }
        }}
      >
        Manage subscription
      </button>
      {err ? (
        <span className="security-manage-subscription__error" role="alert">
          {err}{" "}
          <a href="https://steerly.ai/contact-enterprise" target="_blank" rel="noopener noreferrer">
            Contact us
          </a>
        </span>
      ) : null}
    </span>
  );
}

function Metric({ label, value }: { label: string; value: number | string | ReactNode }) {
  return (
    <article className="metric-card">
      <span>{label}</span>
      <b>{value}</b>
    </article>
  );
}

function CardList({
  items,
  empty,
  approvalActions,
  onTestRule,
  onShowRaw,
  onCreateException,
}: {
  items: TriageItem[];
  empty: string;
  approvalActions: (id: string, decision: string, note?: string) => Promise<void>;
  onTestRule: (item: TriageItem) => void;
  onShowRaw: (item: TriageItem) => void;
  onCreateException: (item: TriageItem) => void;
}) {
  if (!items.length) return <p className="empty-state">{empty}</p>;
  return (
    <>
      {items.map((item) => {
        const severity = getString(item, ["severity", "riskLevel"]) || "info";
        const decision = getString(item, ["decision", "status", "verdict"]) || "recorded";
        const ruleId = getString(item, ["ruleId", "rule", "policyRuleId"]);
        const repo = getString(item, ["repository", "repo", "root"]);
        const agent = getString(item, ["agent", "requester", "actor"]);
        const command = getString(item, ["command", "commandPreview", "requestedAction"]);
        const tool = getString(item, ["tool", "toolName", "name"]);
        const filePath = getString(item, ["filePath", "path"]);
        const sessionId = getString(item, ["sessionId"]);
        const pullRequestUrl = getString(item, ["pullRequestUrl", "url"]);
        const isPrRiskCard = getString(item, ["kind"]) === "pr-risk" || item.__source === "pr-risk";
        const reason =
          getString(item, ["reason", "summary", "message", "error"]) ||
          getString(item, ["recordedAt", "createdAt", "ts"]);
        const approvalId = getApprovalId(item);
        const title = cardTitle(item, command, tool, filePath);

        return (
          <article key={item.id} className="security-card security-triage-card">
            <header>
              <span className={`risk-pill ${riskClass(severity)}`}>{severity}</span>
              <span className={`status-chip ${decisionClass(decision)}`}>{decision}</span>
              {ruleId ? <span className="rule-chip">{ruleId}</span> : null}
            </header>
            <strong>
              {isPrRiskCard && pullRequestUrl ? (
                <a href={pullRequestUrl} target="_blank" rel="noreferrer">
                  {title}
                </a>
              ) : (
                title
              )}
            </strong>
            <span>
              {[repo, agent].filter(Boolean).join(" - ") || item.__source}
              {sessionId ? (
                <>
                  {" "}
                  <a
                    className="security-session-link"
                    href={`/?session=${encodeURIComponent(sessionId)}`}
                  >
                    {sessionId}
                  </a>
                </>
              ) : null}
            </span>
            {command ? <code>{command}</code> : null}
            {tool || filePath ? (
              <dl className="security-card-meta">
                {tool ? (
                  <>
                    <dt>Tool</dt>
                    <dd>{tool}</dd>
                  </>
                ) : null}
                {filePath ? (
                  <>
                    <dt>File</dt>
                    <dd>{filePath}</dd>
                  </>
                ) : null}
              </dl>
            ) : null}
            {reason ? <p>{reason}</p> : null}
            <div className="security-card-actions">
              {approvalId ? (
                <ApprovalActions approvalId={approvalId} onDecide={approvalActions} />
              ) : null}
              <button className="button secondary" type="button" onClick={() => onTestRule(item)}>
                <Play size={14} aria-hidden="true" />
                Test rule
              </button>
              <button
                className="button secondary"
                type="button"
                onClick={() => onCreateException(item)}
              >
                <ShieldAlert size={14} aria-hidden="true" />
                Create exception
              </button>
              <button className="button secondary" type="button" onClick={() => onShowRaw(item)}>
                <FileJson size={14} aria-hidden="true" />
                Show raw
              </button>
            </div>
          </article>
        );
      })}
    </>
  );
}

function ApprovalActions({
  approvalId,
  onDecide,
}: {
  approvalId: string;
  onDecide: (id: string, decision: string, note?: string) => Promise<void>;
}) {
  const [denying, setDenying] = useState(false);
  const [note, setNote] = useState("");
  const [attemptedEmpty, setAttemptedEmpty] = useState(false);
  const [busy, setBusy] = useState(false);
  const trimmedNote = note.trim();

  async function handleApprove() {
    setBusy(true);
    try {
      await onDecide(approvalId, "approved");
    } finally {
      setBusy(false);
    }
  }

  async function handleConfirmDeny() {
    if (!trimmedNote) {
      setAttemptedEmpty(true);
      return;
    }
    setBusy(true);
    try {
      await onDecide(approvalId, "denied", trimmedNote);
      setDenying(false);
      setNote("");
      setAttemptedEmpty(false);
    } finally {
      setBusy(false);
    }
  }

  if (!denying) {
    return (
      <>
        <button
          className="button secondary"
          type="button"
          onClick={() => setDenying(true)}
          disabled={busy}
        >
          <Ban size={14} aria-hidden="true" />
          Deny
        </button>
        <button
          className="button primary"
          type="button"
          onClick={() => void handleApprove()}
          disabled={busy}
        >
          <Check size={14} aria-hidden="true" />
          Approve
        </button>
      </>
    );
  }

  return (
    <div className="security-deny-form">
      <label>
        Reason
        <textarea
          aria-label="Deny reason"
          value={note}
          rows={2}
          onChange={(event) => {
            setNote(event.target.value);
            if (event.target.value.trim()) setAttemptedEmpty(false);
          }}
        />
      </label>
      {attemptedEmpty && !trimmedNote ? (
        <p className="inline-error">Reason required to deny.</p>
      ) : null}
      <div className="security-deny-form__actions">
        <button
          className="button secondary"
          type="button"
          onClick={() => {
            setDenying(false);
            setNote("");
            setAttemptedEmpty(false);
          }}
          disabled={busy}
        >
          Cancel
        </button>
        <button
          className="button primary"
          type="button"
          disabled={!trimmedNote || busy}
          onClick={() => void handleConfirmDeny()}
        >
          Confirm deny
        </button>
      </div>
    </div>
  );
}

function SimulatorPanel({
  state,
  busy,
  onChange,
  onClose,
  onRun,
}: {
  state: SimulatorState;
  busy: boolean;
  onChange: (state: SimulatorState) => void;
  onClose: () => void;
  onRun: () => void;
}) {
  return (
    <aside className="security-drawer" aria-label="Policy simulator">
      <header>
        <div>
          <p className="eyebrow">Policy simulator</p>
          <h2>Test rule</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Close simulator"
          onClick={onClose}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>
      <label>
        Kind
        <select
          value={state.kind}
          onChange={(event) => onChange({ ...state, kind: event.target.value })}
          disabled={busy}
        >
          <option value="command">command</option>
          <option value="file.write">file.write</option>
          <option value="file.read">file.read</option>
          <option value="file.edit">file.edit</option>
        </select>
      </label>
      <label>
        Content
        <textarea
          value={state.content}
          onChange={(event) => onChange({ ...state, content: event.target.value })}
          rows={5}
          disabled={busy}
        />
      </label>
      <label>
        File path
        <input
          value={state.filePath}
          onChange={(event) => onChange({ ...state, filePath: event.target.value })}
          disabled={busy}
        />
      </label>
      <button className="button primary" type="button" disabled={busy} onClick={onRun}>
        <Play size={14} aria-hidden="true" />
        Run simulation
      </button>
      {state.error ? <p className="inline-error">{state.error}</p> : null}
      {state.result ? (
        <pre className="security-json">{JSON.stringify(state.result, null, 2)}</pre>
      ) : null}
    </aside>
  );
}

function RawPanel({ item, onClose }: { item: TriageItem; onClose: () => void }) {
  // Server is already responsible for redaction (DLP/secret-masking happens
  // server-side before the event is emitted). Rendering the raw shape
  // verbatim keeps the UI honest — a duplicate client-side redactor used to
  // blank legitimate `content` fields.
  return (
    <aside className="security-drawer" aria-label="Raw event">
      <header>
        <div>
          <p className="eyebrow">Server-redacted event</p>
          <h2>Raw JSON</h2>
        </div>
        <button className="icon-button" type="button" aria-label="Close raw JSON" onClick={onClose}>
          <X size={16} aria-hidden="true" />
        </button>
      </header>
      <pre className="security-json">{JSON.stringify(item, null, 2)}</pre>
    </aside>
  );
}

function CreateExceptionDrawer({
  draft,
  isAdmin,
  roleLoading,
  busy,
  error,
  onChange,
  onClose,
  onSubmit,
}: {
  draft: ExceptionDraft;
  isAdmin: boolean;
  roleLoading: boolean;
  busy: boolean;
  error: string | null;
  onChange: (draft: ExceptionDraft) => void;
  onClose: () => void;
  onSubmit: () => void;
}) {
  const ruleValid = draft.ruleId.trim().length > 0;
  const reasonValid = draft.reason.trim().length > 0;
  const expiresValid = (() => {
    const parsed = Date.parse(draft.expiresAt);
    return !Number.isNaN(parsed) && parsed > Date.now();
  })();
  // Submission is NOT gated on the role.
  //
  // The previous attempt gated it on `commandPattern` being present, which
  // looked scoped but was not: the drawer SEEDS that field from the event, so
  // it is populated the moment the drawer opens and the gate was always on —
  // locking users out of filing an ordinary exception record whenever `myRole`
  // hung, which is exactly what it claimed to prevent.
  //
  // The gate is unnecessary because the field is blank AND disabled unless the
  // role has resolved to admin, and the payload builder sends the pattern only
  // for a resolved admin. So what the user can see is what gets sent, in every
  // role state — and no state blocks filing the record itself.
  const canSubmit = ruleValid && reasonValid && expiresValid && !busy;

  return (
    <aside className="security-drawer" aria-label="Create exception">
      <header>
        <div>
          <p className="eyebrow">Exception</p>
          <h2>Create exception from event</h2>
        </div>
        <button
          className="icon-button"
          type="button"
          aria-label="Close exception form"
          onClick={onClose}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </header>

      <label>
        Rule id
        <input
          value={draft.ruleId}
          onChange={(event) => onChange({ ...draft, ruleId: event.target.value })}
        />
      </label>
      <label>
        Reason
        <textarea
          value={draft.reason}
          rows={3}
          onChange={(event) => onChange({ ...draft, reason: event.target.value })}
        />
      </label>
      <label>
        Scope
        <select
          value={draft.scope}
          onChange={(event) =>
            onChange({ ...draft, scope: event.target.value as ExceptionDraft["scope"] })
          }
        >
          <option value="repo">repo</option>
          <option value="agent">agent</option>
          <option value="global">global</option>
          <option value="command">command</option>
        </select>
      </label>
      <label>
        Repo
        <input
          value={draft.repo}
          onChange={(event) => onChange({ ...draft, repo: event.target.value })}
        />
      </label>
      <label>
        Agent
        <input
          value={draft.agent}
          onChange={(event) => onChange({ ...draft, agent: event.target.value })}
        />
      </label>
      {/* This is the ONE field on the form that weakens enforcement: a command
          pattern here is compiled and matched ahead of every deny rule. Say so,
          say who may set it, and disable it for everyone else — the mutation
          admin-gates it server-side, so the alternative is a member filling the
          field in and collecting a 403 on submit. */}
      <label>
        Command pattern <span className="sr-field-tag">admin · weakens enforcement</span>
        <input
          // Blank for non-admins: the value is stripped from the payload, and
          // showing the seeded command in a disabled box implies it will be
          // attached to the exception when it will not.
          value={isAdmin ? (draft.commandPattern ?? "") : ""}
          disabled={!isAdmin || roleLoading}
          placeholder={
            isAdmin ? "npm install — matches the whole command" : "admin only — leave blank"
          }
          onChange={(event) => onChange({ ...draft, commandPattern: event.target.value })}
        />
        <span className="sr-field-help">
          {roleLoading ? (
            <>Checking your role — the command pattern field unlocks if you are an org admin.</>
          ) : isAdmin ? (
            <>
              Commands matching this regex skip every deny rule until the exception expires. It
              matches the <strong>whole command</strong>, so <code>npm install</code> excepts
              exactly that and not <code>rm -rf / # npm install</code>. For a family, say so:{" "}
              <code>npm (install|ci).*</code>. Leave it blank to log a scoped exception without
              changing what the engine blocks.
            </>
          ) : (
            <>
              Only an org admin can attach a command pattern. Your exception will be recorded
              without changing what the engine blocks.
            </>
          )}
        </span>
      </label>
      <label>
        Ticket URL
        <input
          value={draft.ticketUrl}
          onChange={(event) => onChange({ ...draft, ticketUrl: event.target.value })}
        />
      </label>
      <label>
        Expires at (ISO)
        <input
          value={draft.expiresAt}
          onChange={(event) => onChange({ ...draft, expiresAt: event.target.value })}
        />
      </label>
      {!expiresValid ? (
        <p className="inline-error">Expires at must be a future ISO timestamp.</p>
      ) : null}
      {error ? <p className="inline-error">{error}</p> : null}
      <button className="button primary" type="button" disabled={!canSubmit} onClick={onSubmit}>
        <Plus size={14} aria-hidden="true" />
        Create exception
      </button>
    </aside>
  );
}

function buildSnapshotItems(room: Record<string, unknown>): TriageItem[] {
  const triage = recordValue(room.triage);
  const explicitSource = normalizeList(room.triageItems);
  const triageSource = normalizeList(triage?.items);
  const explicit = (explicitSource.length ? explicitSource : triageSource).map((item) =>
    normalizeItem(item, "triage")
  );
  if (explicit.length) {
    return explicit;
  }

  const audit = normalizeList(room.commandAudit).map((item) => normalizeItem(item, "audit"));
  return [
    ...normalizeList(room.pendingApprovals || room.approvals).map((item) =>
      normalizeItem(item, "approval")
    ),
    ...[
      ...normalizeList(room.reviewableAsks),
      ...normalizeList(room.reviewableAskEvents),
      ...normalizeList(triage?.reviewableAskEvents),
    ].map((item) => normalizeItem(item, "approval")),
    ...[...normalizeList(room.blockedCommands), ...normalizeList(triage?.blockedCommands)].map(
      (item) => normalizeItem(item, "blocked")
    ),
    ...[
      ...normalizeList(room.dlpEvents),
      ...normalizeList(room.dlpFindings),
      ...normalizeList(room.dlpHits),
      ...normalizeList(triage?.dlpHits),
    ].map((item) => normalizeItem(item, "dlp")),
    ...[
      ...normalizeList(room.prRisks),
      ...normalizeList(room.pullRequests),
      ...normalizeList(room.prRiskQueue),
      ...normalizeList(triage?.prRiskQueue),
    ].map((item) => normalizeItem(item, "pr-risk")),
    ...[...normalizeList(room.highRiskSessions), ...normalizeList(triage?.highRiskSessions)].map(
      (item) => normalizeItem(item, "high-risk")
    ),
    ...[
      ...normalizeList(room.memoryHighlights),
      ...normalizeList(room.memoryNotes),
      ...normalizeList(triage?.memoryHighlights),
    ].map((item) => normalizeItem(item, "memory")),
    ...[
      ...normalizeList(room.exceptions),
      ...normalizeList(room.securityExceptions),
      ...normalizeList(triage?.exceptions),
    ].map((item) => normalizeItem(item, "exception")),
    ...audit,
  ];
}

function mergeTriageItems(
  snapshotItems: TriageItem[],
  liveEvents: Array<Record<string, unknown>>
): TriageItem[] {
  const out: TriageItem[] = [];
  const seen = new Set<string>();
  for (const item of [
    ...liveEvents.map((event) => normalizeItem(event, "live")),
    ...snapshotItems,
  ]) {
    if (seen.has(item.id)) continue;
    seen.add(item.id);
    out.push(item);
  }
  // Newest first across buckets — `triageItems[0]` feeds the "Last event"
  // metric and the All-events queue mixes buckets. Items without a
  // parseable timestamp sort last (stable).
  const ts = (item: TriageItem): number => {
    const raw = getString(item, ["lastSeenAt", "recordedAt", "ts", "createdAt", "at"]);
    if (!raw) return -1;
    if (/^\d+$/.test(raw)) return Number(raw);
    const parsed = Date.parse(raw);
    return Number.isNaN(parsed) ? -1 : parsed;
  };
  return out
    .map((item, index) => ({ item, index, t: ts(item) }))
    .sort((a, b) => b.t - a.t || a.index - b.index)
    .map((x) => x.item);
}

function filterByTimeRange(items: TriageItem[], range: TimeRange): TriageItem[] {
  if (range === "all") return items;
  const duration = range === "24h" ? 24 * 60 * 60 * 1000 : 7 * 24 * 60 * 60 * 1000;
  const threshold = Date.now() - duration;
  return items.filter((item) => {
    const ts = getString(item, ["lastSeenAt", "recordedAt", "ts", "createdAt", "at"]);
    if (!ts) return false;
    const parsed = Date.parse(ts);
    return !Number.isNaN(parsed) && parsed >= threshold;
  });
}

function normalizeItem(value: Record<string, unknown>, source: string): TriageItem {
  const id =
    getString(value, ["id", "eventId", "approvalId"]) ||
    `${source}-${JSON.stringify(value).slice(0, 80)}`;
  return {
    ...value,
    id,
    __source: source,
  };
}

function normalizeList(value: unknown): Array<Record<string, unknown>> {
  return Array.isArray(value)
    ? value.filter((item): item is Record<string, unknown> =>
        Boolean(item && typeof item === "object")
      )
    : [];
}

function recordValue(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function filterQueue(items: TriageItem[], queue: QueueKey): TriageItem[] {
  if (queue === "all") return items;
  return items.filter((item) => itemMatchesQueue(item, queue));
}

function countQueues(items: TriageItem[]): Record<QueueKey, number> {
  return QUEUES.reduce(
    (counts, queue) => {
      counts[queue.key] = filterQueue(items, queue.key).length;
      return counts;
    },
    {} as Record<QueueKey, number>
  );
}

// Strict, kind-based queue classification.
//
// Background: a previous substring-based matcher meant a normal
// `command-event` with a `decision: "error"` body would leak into the
// Exceptions queue (haystack scan hit the word "error"). Anchoring on the
// server-assigned `kind` field is the contract.
function itemMatchesQueue(item: TriageItem, queue: QueueKey): boolean {
  const kind = getString(item, ["kind"]);
  switch (queue) {
    case "needs-review":
      return (
        kind === "pending-approval" ||
        kind === "reviewable-ask-event" ||
        (!kind && fallbackReviewable(item))
      );
    case "blocked":
      return kind === "blocked-command" || (!kind && fallbackBlocked(item));
    case "dlp":
      return kind === "dlp-hit" || (!kind && fallbackDlp(item));
    case "pr-risk":
      return kind === "pr-risk" || (!kind && fallbackPrRisk(item));
    case "high-risk":
      return kind === "high-risk-session" || (!kind && fallbackHighRisk(item));
    case "memory":
      return kind === "memory-highlight" || (!kind && fallbackMemory(item));
    case "exceptions":
      return kind === "security-exception" || (!kind && fallbackException(item));
    default:
      return false;
  }
}

// Fallbacks: only used when the item has no `kind` (raw live-event from the
// admin activity stream that hasn't been normalized server-side). These now
// match on the item's source bucket exactly, not on a fuzzy substring scan
// across multiple fields — which is what created the original P1 bug.
function fallbackReviewable(item: TriageItem): boolean {
  if (item.__source === "approval") return true;
  const status = getString(item, ["status"]).toLowerCase();
  const decision = getString(item, ["decision", "verdict"]).toLowerCase();
  return (
    status === "pending" ||
    status === "pending-approval" ||
    decision === "ask" ||
    Boolean(getApprovalId(item))
  );
}

function fallbackBlocked(item: TriageItem): boolean {
  if (item.__source === "blocked") return true;
  const status = getString(item, ["status"]).toLowerCase();
  const decision = getString(item, ["decision", "verdict"]).toLowerCase();
  return (
    ["deny", "denied", "blocked"].includes(status) ||
    ["deny", "denied", "blocked"].includes(decision)
  );
}

function fallbackDlp(item: TriageItem): boolean {
  if (item.__source === "dlp") return true;
  const ruleId = getString(item, ["ruleId", "rule", "policyRuleId"]).toLowerCase();
  return ruleId.startsWith("secrets/") || ruleId.startsWith("dlp/");
}

function fallbackPrRisk(item: TriageItem): boolean {
  return item.__source === "pr-risk";
}

function fallbackHighRisk(item: TriageItem): boolean {
  if (item.__source === "high-risk") return true;
  const risk = getString(item, ["riskLevel", "severity"]).toLowerCase();
  return (
    ["high", "critical"].includes(risk) && Boolean(getString(item, ["sessionId", "title", "goal"]))
  );
}

function fallbackMemory(item: TriageItem): boolean {
  return item.__source === "memory";
}

function fallbackException(item: TriageItem): boolean {
  return item.__source === "exception";
}

function getApprovalId(item: TriageItem): string | null {
  const status = getString(item, ["status"]).toLowerCase();
  const kind = getString(item, ["kind"]).toLowerCase();
  const decision = getString(item, ["decision", "verdict"]).toLowerCase();
  // Reject if the item carries a non-"ask" decision — the approval was
  // already approved/denied, so Approve/Deny buttons must not appear even
  // if a stale live event still has kind === "pending-approval".
  if (decision && decision !== "ask") return null;
  const isPending =
    status === "pending" || status === "pending-approval" || kind === "pending-approval";
  if (!isPending) return null;
  const nested = item.approval;
  if (nested && typeof nested === "object") {
    const nestedId = getString(nested as Record<string, unknown>, ["id"]);
    if (nestedId) return nestedId;
  }
  return getString(item, ["approvalId", "id"]) || null;
}

function cardTitle(item: TriageItem, command: string, tool: string, filePath: string): string {
  return (
    command ||
    tool ||
    filePath ||
    getString(item, ["title", "goal", "type", "id"]) ||
    "Security event"
  );
}

function getString(item: Record<string, unknown> | undefined, keys: string[]): string {
  if (!item) return "";
  for (const key of keys) {
    const value = item[key];
    if (typeof value === "string" && value.trim()) return value;
    if (typeof value === "number" || typeof value === "boolean") return String(value);
  }
  return "";
}

function numberMetric(value: unknown, fallback: number): number {
  return typeof value === "number" ? value : fallback;
}

function normalizeEnforcement(value: unknown): Array<{
  id: string;
  label: string;
  state: string;
  detail: string;
}> {
  if (!value || typeof value !== "object") return [];
  return Object.entries(value as Record<string, unknown>).map(([key, raw]) => {
    if (raw && typeof raw === "object") {
      const record = raw as Record<string, unknown>;
      return {
        id: key,
        label: getString(record, ["label", "name"]) || labelize(key),
        state:
          getString(record, ["state", "status", "mode"]) ||
          (record.enabled === true ? "enabled" : "disabled"),
        detail: getString(record, ["detail", "description", "message"]),
      };
    }
    return {
      id: key,
      label: labelize(key),
      state: String(raw),
      detail: "",
    };
  });
}

function buildSimulatorEvent(state: SimulatorState): Record<string, unknown> {
  const event: Record<string, unknown> = { kind: state.kind };
  if (state.kind === "command") event.command = state.content;
  else event.content = state.content;
  if (state.filePath.trim()) event.filePath = state.filePath.trim();
  return event;
}

function formatTimestamp(value: unknown): string {
  // toLocaleString (not toLocaleTimeString) so the rendered timestamp keeps
  // the date context. The "All time" / "Last 7d" filters can surface events
  // from days ago, where a bare "10:42:13" is ambiguous; matches the
  // AdminConsoleView formatting too.
  if (!value) return "";
  if (typeof value === "number") return new Date(value).toLocaleString();
  const parsed = Date.parse(String(value));
  if (Number.isNaN(parsed)) return String(value);
  return new Date(parsed).toLocaleString();
}

function riskClass(risk: unknown): string {
  const key = String(risk || "").toLowerCase();
  if (key === "critical") return "risk-critical";
  if (key === "high") return "risk-high";
  if (key === "medium") return "risk-medium";
  if (key === "low") return "risk-low";
  return "muted";
}

function decisionClass(decision: unknown): string {
  const key = String(decision || "").toLowerCase();
  if (key === "deny" || key === "denied" || key === "blocked") return "status-blocked";
  if (key === "ask" || key === "pending") return "status-needs-review";
  if (key === "approved" || key === "allow") return "status-passed";
  return "muted";
}

function queueStatusClass(queue: QueueKey): string {
  if (queue === "blocked" || queue === "exceptions") return "status-blocked";
  if (queue === "needs-review" || queue === "dlp" || queue === "pr-risk")
    return "status-needs-review";
  if (queue === "high-risk") return "status-flagged";
  return "muted";
}

function queueLabel(queue: QueueKey): string {
  return QUEUES.find((item) => item.key === queue)?.label || "Events";
}

function errorStatus(error: unknown): number | undefined {
  if (
    error &&
    typeof error === "object" &&
    typeof (error as { status?: unknown }).status === "number"
  ) {
    return (error as { status: number }).status;
  }
  return undefined;
}

function formatSecurityRoomError(error: unknown): string {
  const status = errorStatus(error);
  const message = error instanceof Error ? error.message : "Security room unavailable";
  if (status === 401 || /unauthorized|missing_token/i.test(message)) {
    return "Security Room access is not authorized for this browser session. Sign in again with a Clerk account on the security plan, then refresh.";
  }
  if (status === 402 || /plan_required|payment|required|plan/i.test(message)) {
    return "Security Room requires the security plan for this signed-in account.";
  }
  return message;
}

function labelize(value: string): string {
  // Handle camelCase too (e.g. `defaultEnforce` → "Default Enforce"). The
  // enforcement-posture payload uses camelCase keys for several fields and
  // the previous implementation only split on `-`/`_`, leaving keys like
  // "defaultEnforce" rendered as the raw identifier with a leading cap.
  return value
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
